// Pure helpers used by SingleForm — option list builders + seed
// derivation. Lifted out so the form file itself stays under the
// 500-line cap while still letting the helpers live next to the
// component that consumes them.

import type { ReactNode } from 'react';
import type {
  SegmentedOption,
  SelectOption,
} from '@viquard/doc-ui/components';
import type {
  CalendarEvent,
  CalendarEventLink,
  CalendarKindMap,
} from '../../types.js';
import { isDateOnlyISO, localDateKey, localTime } from '../../utils/dateUtils.js';
import { eventInstanceKey } from '../../utils/identity.js';
import type { CalendarMessages } from '../../i18n.js';
import { buildDefaultGroupOptions, PRIVATE_GROUP_SENTINEL } from './group-helpers.js';
import { plusMinutes } from './helpers.js';
import type { EventComposerSubmitDraft } from './types.js';
import {
  resolveKindDefaults,
  type CalendarKindBehaviorMap,
} from '../../utils/kindBehavior.js';
import type {
  CalendarLocationType,
  CalendarMeetingProvider,
} from '../../utils/location.js';

// Re-export so SingleForm.tsx can keep its existing import surface
// while the canonical sentinel lives in `group-helpers.ts` (shared by
// BatchForm + AIForm too).
export { PRIVATE_GROUP_SENTINEL };

export interface SingleFormSeeds {
  readonly kind: string;
  readonly title: string;
  readonly allDay: boolean;
  readonly durationMinutes: number;
  readonly start: string | null;
  readonly end: string | null;
  readonly groupId: string | null;
  readonly crossPosted: readonly string[];
  readonly meta: string;
  readonly recurring: boolean;
  readonly recurrence: string | null;
  readonly reminderMinutesBefore: number | null;
  readonly link: CalendarEventLink | null;
  readonly locationType: CalendarLocationType | null;
  readonly meetingProvider: CalendarMeetingProvider;
  readonly meetingDetail: string;
  readonly locationText: string;
}

export interface RelatedEventMatch {
  readonly event: CalendarEvent;
  readonly exact: boolean;
  readonly score: number;
}

/** Walk: existing event → draft → first non-default theme entry. The
 *  `default` key in kindTheme is the fallback theme used when an
 *  unknown kind comes through, NOT a valid picker option (the
 *  segmented / select renders only `k !== 'default'` entries), so we
 *  skip past it. Any candidate kind not present in the active
 *  kindTheme also falls through to the fallback so the picker never
 *  renders blank. */
export function computeSingleFormSeeds(args: {
  event?: CalendarEvent | null | undefined;
  draft?: Partial<EventComposerSubmitDraft> | undefined;
  kindTheme: CalendarKindMap;
  kindBehavior: CalendarKindBehaviorMap;
  defaultDurationMinutes: number;
}): SingleFormSeeds {
  const { event, draft, kindTheme, kindBehavior, defaultDurationMinutes } = args;
  const fallbackKind =
    Object.keys(kindTheme).find((k) => k !== 'default') ?? 'default';
  const candidateKind = event?.kind ?? draft?.kind ?? null;
  const kind =
    candidateKind !== null &&
    candidateKind !== 'default' &&
    kindTheme[candidateKind] !== undefined
      ? candidateKind
      : fallbackKind;
  const kindDefaults = resolveKindDefaults(kindBehavior, kind, {
    fallbackDurationMinutes: defaultDurationMinutes,
  });
  const allDay = event?.allDay ?? draft?.allDay ?? kindDefaults.durationMinutes === 0;
  const start = event?.startsAt ?? draft?.startsAt ?? null;
  // When the seed is a date-only ISO (e.g. mobile FAB seeded with
  // selectedDate), don't synthesise an `endsAt` from `plusMinutes` —
  // that would produce a sub-second instant after midnight and trip
  // `durationFromIso` into reading the seed as all-day, silently
  // flipping the toggle. Leave `end` null so duration falls back to
  // `defaultDurationMinutes` and the user keeps the time controls. For
  // an explicit all-day seed, mirror the start so duration locks to
  // '0' and the toggle stays on.
  const end =
    event?.endsAt ??
    draft?.endsAt ??
    (start === null
      ? null
      : allDay
        ? start
        : isDateOnlyISO(start)
          ? null
          : plusMinutes(start, kindDefaults.durationMinutes));
  const recurrence = event?.recurrence ?? draft?.recurrence ?? kindDefaults.recurrence;
  const recurring =
    event?.recurring ??
    draft?.recurring ??
    (recurrence !== null ? true : kindDefaults.recurring);
  const reminderMinutesBefore =
    event?.reminderMinutesBefore !== undefined
      ? event.reminderMinutesBefore
      : draft?.reminderMinutesBefore !== undefined
        ? draft.reminderMinutesBefore
        : kindDefaults.reminderMinutesBefore;
  return {
    kind,
    title: event?.title ?? draft?.title ?? '',
    allDay,
    durationMinutes: kindDefaults.durationMinutes,
    start,
    end,
    // Default to "no group" so the event is private until the user
    // explicitly attaches it. The previous default (`groups?.[0]`)
    // silently scoped events to the first group in the picker.
    groupId: event?.groupId ?? draft?.groupId ?? null,
    crossPosted: event?.crossPostedTo ?? draft?.crossPostedTo ?? [],
    meta: event?.meta ?? draft?.meta ?? '',
    recurring,
    recurrence,
    reminderMinutesBefore,
    link: (event?.links?.[0] ?? draft?.link ?? null),
    locationType: event?.locationType ?? draft?.locationType ?? null,
    meetingProvider: event?.meetingProvider ?? draft?.meetingProvider ?? 'tencent',
    meetingDetail: event?.meetingDetail ?? draft?.meetingDetail ?? '',
    locationText: event?.locationText ?? draft?.locationText ?? '',
  };
}

export function buildKindSegmentedOptions(
  kindTheme: CalendarKindMap,
): readonly SegmentedOption[] {
  return Object.entries(kindTheme)
    .filter(([k]) => k !== 'default')
    .map(([k, def]) => ({
      value: k,
      label: def.label,
      icon: (
        <span
          className="vq-cal__kind-dot"
          style={{ background: def.color }}
          aria-hidden="true"
        />
      ),
    }));
}

/** doc-ui's `Select` renders `option.icon` inside the listbox panel
 *  but NOT inside the trigger (which shows `selectedOption.label`
 *  verbatim). To keep the coloured-dot affordance visible on the
 *  trigger too, we bake the dot into the `label` ReactNode itself
 *  and skip the separate `icon` slot — otherwise the panel rows
 *  would render two dots side by side. */
export function buildKindSelectOptions(
  kindTheme: CalendarKindMap,
): readonly SelectOption[] {
  return Object.entries(kindTheme)
    .filter(([k]) => k !== 'default')
    .map(([k, def]) => ({
      value: k,
      label: makeKindLabel(def.label, def.color),
      searchText: def.label,
    }));
}

function makeKindLabel(label: string, color: string): ReactNode {
  return (
    <span className="vq-cal__composer-kind-option">
      <span
        className="vq-cal__kind-dot"
        style={{ background: color }}
        aria-hidden="true"
      />
      <span>{label}</span>
    </span>
  );
}

/** Re-export of `buildDefaultGroupOptions` under the SingleForm-facing
 *  name. The shared helper now serves both the SingleForm primary-group
 *  picker and the BatchForm/AIForm "Default group" picker. */
export const buildGroupOptions = buildDefaultGroupOptions;

/** Extract the linked-content options the consumer's `linkOptionsForKind`
 *  callback returned, prepending a sentinel "None" row so the Select
 *  trigger can clear the link. */
export function buildLinkOptions(
  linkOptions: readonly CalendarEventLink[],
  noneLabel: string,
): readonly SelectOption[] {
  return [
    { value: '__none__', label: noneLabel },
    ...linkOptions.map((opt) => ({
      value: `${opt.kind}:${opt.id}`,
      label: opt.label,
      searchText: opt.label,
    })),
  ];
}

/** Resolve the labelled-error message for the title-required + date-
 *  required validation pair. Lives here so SingleForm doesn't have to
 *  duplicate the i18n key strings inline. */
export function pickValidationError(
  title: string,
  hasDate: boolean,
  messages: CalendarMessages,
): string | null {
  if (title.trim() === '') return messages.composerErrorTitleRequired;
  if (!hasDate) return messages.composerErrorDateRequired;
  return null;
}

export function findRelatedEvents(args: {
  readonly event: CalendarEvent;
  readonly events: readonly CalendarEvent[];
  readonly editingEventId?: string | undefined;
  readonly viewerTz?: string | undefined;
}): readonly RelatedEventMatch[] {
  const { event, events, editingEventId, viewerTz } = args;
  const title = normaliseTitle(event.title);
  const dateKey = localDateKey(event.startsAt, viewerTz);
  const timeKey = event.allDay === true || isDateOnlyISO(event.startsAt)
    ? null
    : localTime(event.startsAt, viewerTz);
  const linkKeys = new Set((event.links ?? []).map((link) => `${link.kind}:${link.id}`));

  return events
    .map((candidate): RelatedEventMatch | null => {
      if (candidate.id === event.id || candidate.id === editingEventId) return null;
      if (
        editingEventId !== undefined &&
        candidate.recurrenceMasterId === editingEventId
      ) return null;
      if (candidate.status === 'cancelled') return null;

      const candidateTitle = normaliseTitle(candidate.title);
      const sameTitle = title.length > 0 && candidateTitle === title;
      const titleContains =
        title.length >= 4 &&
        candidateTitle.length >= 4 &&
        (candidateTitle.includes(title) || title.includes(candidateTitle));
      const sameDate = localDateKey(candidate.startsAt, viewerTz) === dateKey;
      const candidateTime = candidate.allDay === true || isDateOnlyISO(candidate.startsAt)
        ? null
        : localTime(candidate.startsAt, viewerTz);
      const sameTime = sameDate && candidateTime === timeKey;
      const sameKind = candidate.kind === event.kind;
      const sameDuration = sameOptionalInstant(candidate.endsAt ?? null, event.endsAt ?? null);
      const sameLink = (candidate.links ?? []).some((link) => linkKeys.has(`${link.kind}:${link.id}`));
      const exact = sameTitle && sameDate && sameTime && sameKind && sameDuration;
      let score = exact ? 100 : 0;
      if (sameLink) score += 80;
      if (sameTitle) score += 48;
      else if (titleContains) score += 28;
      if (sameDate) score += 24;
      if (sameTime) score += 16;
      if (sameKind) score += 8;
      if (!sameLink && !exact && score < 36) return null;
      return { event: candidate, exact, score };
    })
    .filter((match): match is RelatedEventMatch => match !== null)
    .sort((a, b) => b.score - a.score || a.event.startsAt.localeCompare(b.event.startsAt))
    .filter(uniqueRelatedMatch())
    .slice(0, 4);
}

function uniqueRelatedMatch(): (match: RelatedEventMatch) => boolean {
  const seen = new Set<string>();
  return (match) => {
    const key = eventInstanceKey(match.event);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  };
}

function normaliseTitle(value: string): string {
  return value
    .toLocaleLowerCase()
    .replace(/[^\p{Letter}\p{Number}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function sameOptionalInstant(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return a === b;
  const aMs = new Date(a).getTime();
  const bMs = new Date(b).getTime();
  if (Number.isNaN(aMs) || Number.isNaN(bMs)) return a === b;
  return aMs === bMs;
}
